import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Globe } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type PublicLanguagesResponse = {
  defaultLocale: string;
  items: Array<{ code: string; name: string; isDefault?: boolean }>;
};

export function LanguageSwitcher({
  className,
  variant = "compact",
}: {
  className?: string;
  /** "compact" = navbar pill (icon + code). "row" = full-width settings row for the mobile sheet. */
  variant?: "compact" | "row";
}) {
  const qc = useQueryClient();

  const { data } = useQuery<PublicLanguagesResponse>({
    queryKey: ["/api/languages"],
    staleTime: 5 * 60 * 1000,
  });

  const [current, setCurrent] = useState(() => {
    try {
      return (localStorage.getItem("fx.locale") || "").trim().toLowerCase();
    } catch {
      return "";
    }
  });

  const items = Array.isArray(data?.items) ? data!.items : [{ code: "en", name: "English", isDefault: true }];
  const defaultLocale = String(data?.defaultLocale || "en").trim().toLowerCase() || "en";
  const active = current || defaultLocale;

  const activeItem = items.find((i) => String(i.code).toLowerCase() === active);
  const label = (activeItem?.code || active).toUpperCase();
  const activeName = activeItem?.name || label;

  const setLocale = async (code: string) => {
    const next = String(code || "").trim().toLowerCase();
    try {
      if (!next || next === defaultLocale || next === "en") localStorage.removeItem("fx.locale");
      else localStorage.setItem("fx.locale", next);
    } catch {
      // ignore
    }

    setCurrent(next === defaultLocale || next === "en" ? "" : next);

    // App uses react-query with staleTime=Infinity; force reload of data.
    await qc.invalidateQueries();
  };

  const isRow = variant === "row";

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Language: ${activeName}`}
          className={cn(
            "group text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            isRow
              ? "flex h-12 w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-left transition-colors hover:border-primary/30 hover:bg-white/[0.07] data-[state=open]:border-primary/40 data-[state=open]:bg-white/[0.07]"
              : "inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-2.5 transition-all hover:border-primary/40 hover:bg-white/10 data-[state=open]:border-primary/50 data-[state=open]:bg-white/10 sm:px-3",
            className,
          )}
        >
          <Globe className="h-4 w-4 shrink-0 text-primary" />
          {isRow ? (
            <>
              <span className="flex-1 truncate text-[12px] font-black uppercase tracking-[0.2em]">
                Language
              </span>
              <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                {label}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </>
          ) : (
            <>
              <span className="text-[11px] font-black uppercase tracking-[0.16em]">{label}</span>
              <ChevronDown className="hidden h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180 sm:block" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={isRow ? "start" : "end"}
        sideOffset={8}
        className={cn(
          "rounded-2xl border-white/10 bg-[#0b1120] p-1.5 text-white shadow-2xl shadow-black/60",
          isRow ? "w-[var(--radix-dropdown-menu-trigger-width)]" : "w-64",
        )}
      >
        <DropdownMenuLabel className="flex items-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-gray-500">
          <Globe className="h-3 w-3" />
          Choose language
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />

        {items.map((it) => {
          const code = String(it.code || "").toLowerCase();
          const isActive = code === active;
          return (
            <DropdownMenuItem
              key={code}
              onClick={() => setLocale(code)}
              className={cn(
                "cursor-pointer rounded-xl px-3 py-2.5 transition-colors focus:bg-white/5",
                isActive && "bg-primary/10",
              )}
            >
              <span className="flex flex-1 items-baseline gap-2">
                <span className="text-[11px] font-black uppercase tracking-widest">{code.toUpperCase()}</span>
                <span className="truncate text-xs text-gray-400">{String(it.name || code)}</span>
              </span>
              {isActive ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
